import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Button from '../../components/shared/Button'
import Loader from '../../components/shared/Loader'
import Section from '../../layouts/Section'
import SectionBody from '../../layouts/SectionBody'
import {
  getSpiderAutoImportConfig,
  getSpiderProductSources,
  runSpiderAutoImportNow,
  updateSpiderAutoImportConfig,
} from '../../utils/Apis/products'
import { withErrorHandling } from '../../utils/withErrorHandling'

const DEFAULT_FORM = {
  enabled: false,
  sources: ['lcsc', 'digikey'],
  intervalMs: 3600000,
  startDelayMs: 30000,
  pageSize: 100,
  maxPagesPerRun: 10,
}

/**
 * 格式化后台显示时间。
 * @param {string|undefined} value ISO 时间
 * @returns {string} 本地时间
 */
const formatDate = (value) =>
  value ? new Date(value).toLocaleString('zh-CN') : '-'

/**
 * 将接口配置转换为页面表单数据。
 * @param {Record<string, any>} config 接口配置
 * @returns {Record<string, any>} 表单数据
 */
const normalizeForm = (config = {}) => ({
  enabled: config.enabled === true,
  sources: Array.isArray(config.sources) ? config.sources : DEFAULT_FORM.sources,
  intervalMs: Number(config.intervalMs || DEFAULT_FORM.intervalMs),
  startDelayMs: Number(config.startDelayMs || DEFAULT_FORM.startDelayMs),
  pageSize: Number(config.pageSize || DEFAULT_FORM.pageSize),
  maxPagesPerRun: Number(config.maxPagesPerRun || DEFAULT_FORM.maxPagesPerRun),
})

/**
 * 爬虫商品自动导入后台配置页面。
 * @returns {JSX.Element} 页面组件
 */
const SpiderAutoImportSettings = () => {
  const [form, setForm] = useState(DEFAULT_FORM)
  const [sources, setSources] = useState([])
  const [syncRuns, setSyncRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)

  /**
   * 加载自动导入配置和可用来源。
   * @param {boolean} showLoading 是否显示加载状态
   */
  const loadConfig = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setLoading(true)
    }
    try {
      const [configResponse, sourceResponse] = await Promise.all([
        getSpiderAutoImportConfig(),
        getSpiderProductSources(),
      ])
      setForm(normalizeForm(configResponse.data))
      setSyncRuns(configResponse.syncRuns || [])
      setSources(sourceResponse.data || [])
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message)
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    document.title = 'Spider Auto Import - Admin'
    loadConfig(true)
  }, [loadConfig])

  /**
   * 合并可用来源和已配置来源，避免配置来源暂不可用时从界面丢失。
   */
  const sourceOptions = useMemo(() => {
    const optionMap = new Map()
    sources.forEach((item) => {
      optionMap.set(item.code, {
        code: item.code,
        name: item.name || item.code,
        storage: item.storage || '-',
        available: true,
      })
    })
    form.sources.forEach((source) => {
      if (!optionMap.has(source)) {
        optionMap.set(source, {
          code: source,
          name: source.toUpperCase(),
          storage: '-',
          available: false,
        })
      }
    })
    return Array.from(optionMap.values())
  }, [form.sources, sources])

  /**
   * 更新普通表单字段。
   * @param {string} field 字段名
   * @param {boolean|number} value 字段值
   */
  const updateField = (field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }))
  }

  /**
   * 切换自动导入来源。
   * @param {string} source 来源编码
   */
  const toggleSource = (source) => {
    setForm((currentForm) => {
      const hasSource = currentForm.sources.includes(source)
      return {
        ...currentForm,
        sources: hasSource
          ? currentForm.sources.filter((item) => item !== source)
          : [...currentForm.sources, source],
      }
    })
  }

  /**
   * 保存自动导入配置。
   */
  const handleSave = withErrorHandling(async () => {
    if (form.sources.length <= 0) {
      return toast.warning('请至少选择一个自动导入来源')
    }
    setSaving(true)
    try {
      const response = await updateSpiderAutoImportConfig(form)
      setForm(normalizeForm(response.data))
      toast.success(response.message || '自动导入配置已保存')
      await loadConfig()
    } finally {
      setSaving(false)
    }
  })

  /**
   * 立即执行一轮自动导入。
   */
  const handleRunNow = withErrorHandling(async () => {
    setRunning(true)
    try {
      const response = await runSpiderAutoImportNow()
      if (response.started === false) {
        toast.info(response.message || '自动导入正在执行中')
      } else {
        toast.success(response.message || '自动导入已开始后台执行')
      }
    } finally {
      setRunning(false)
    }
  })

  return (
    <div>
      <Section title="Spider Auto Import" className="border-none lg:pb-1" />
      <SectionBody className="custom-scroll max-h-[75vh] overflow-auto">
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="rounded-lg border border-primary/20 bg-white p-4">
              <div className="grid gap-4 lg:grid-cols-2">
                <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(event) =>
                      updateField('enabled', event.target.checked)
                    }
                    className="size-4"
                  />
                  启用自动导入
                </label>
                <div className="flex justify-start gap-2 lg:justify-end">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Config'}
                  </Button>
                  <Button
                    onClick={handleRunNow}
                    disabled={running || saving}
                    className="bg-green-600 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {running ? 'Running...' : 'Run Once'}
                  </Button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm font-medium">
                  执行间隔 ms
                  <input
                    type="number"
                    min="60000"
                    value={form.intervalMs}
                    onChange={(event) =>
                      updateField('intervalMs', Number(event.target.value))
                    }
                    className="h-10 rounded-md border px-3 outline-none focus:ring-2"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm font-medium">
                  启动延迟 ms
                  <input
                    type="number"
                    min="0"
                    value={form.startDelayMs}
                    onChange={(event) =>
                      updateField('startDelayMs', Number(event.target.value))
                    }
                    className="h-10 rounded-md border px-3 outline-none focus:ring-2"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm font-medium">
                  每页数量
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.pageSize}
                    onChange={(event) =>
                      updateField('pageSize', Number(event.target.value))
                    }
                    className="h-10 rounded-md border px-3 outline-none focus:ring-2"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm font-medium">
                  每轮最大页数
                  <input
                    type="number"
                    min="1"
                    value={form.maxPagesPerRun}
                    onChange={(event) =>
                      updateField('maxPagesPerRun', Number(event.target.value))
                    }
                    className="h-10 rounded-md border px-3 outline-none focus:ring-2"
                  />
                </label>
              </div>

              <div className="mt-5">
                <p className="mb-2 text-sm font-medium text-gray-700">
                  自动导入来源
                </p>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {sourceOptions.map((item) => (
                    <label
                      key={item.code}
                      className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm"
                    >
                      <span>
                        {item.name}
                        <span className="ml-2 text-xs text-gray-500">
                          {item.storage}
                        </span>
                        {!item.available && (
                          <span className="ml-2 text-xs text-amber-600">
                            未发现
                          </span>
                        )}
                      </span>
                      <input
                        type="checkbox"
                        checked={form.sources.includes(item.code)}
                        onChange={() => toggleSource(item.code)}
                        className="size-4"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-primary/20 bg-white">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3">来源</th>
                    <th className="px-3 py-3">状态</th>
                    <th className="px-3 py-3">开始时间</th>
                    <th className="px-3 py-3">完成时间</th>
                    <th className="px-3 py-3">起始页码</th>
                    <th className="px-3 py-3">下次页码</th>
                    <th className="px-3 py-3">新增</th>
                    <th className="px-3 py-3">错误</th>
                  </tr>
                </thead>
                <tbody>
                  {syncRuns.map((run) => (
                    <tr key={run._id} className="border-t border-gray-100">
                      <td className="px-3 py-3">{run.source}</td>
                      <td className="px-3 py-3">
                        <span
                          className={
                            run.status === 'success'
                              ? 'text-green-600'
                              : 'text-red-600'
                          }
                        >
                          {run.status === 'success' ? '成功' : '失败'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {formatDate(run.startedAt)}
                      </td>
                      <td className="px-3 py-3">
                        {formatDate(run.finishedAt)}
                      </td>
                      <td className="px-3 py-3">{run.fromPage || 1}</td>
                      <td className="px-3 py-3">{run.nextPage || 1}</td>
                      <td className="px-3 py-3">
                        {run.importedCount || 0}
                      </td>
                      <td className="max-w-[260px] break-words px-3 py-3 text-red-600">
                        {run.error || '-'}
                      </td>
                    </tr>
                  ))}
                  {syncRuns.length === 0 && (
                    <tr>
                      <td className="px-3 py-8 text-center" colSpan="8">
                        暂无自动导入记录
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionBody>
    </div>
  )
}

export default SpiderAutoImportSettings
